import { Box, Text } from '@wix/design-system';
import React from 'react';
import { SaleBanner as PlatformSaleBanner } from '@wix/premium-purchase-platform/@components';
import {
  EclipseBanner,
  type EclipseBannerProps,
  SaleTimer,
  useCurrentModule,
  useResellerBranding,
  useSaleBannerType,
  useWelcomeGiftAudience,
  type SaleBannerType,
  extractNumericRate,
} from '@wix/package-picker-shared-eclipse';
import { EclipsePackagePickerModule } from '../../modules/eclipse-package-picker.module';
import {
  DATA_HOOKS,
  miniPpDefault,
  WELCOME_GIFT_BANNER_KEYS,
  type WelcomeGiftStage,
} from '../QuickPayLightbox/constants';
import s from './SaleBanner.scss';

const WELCOME_GIFT_STAGES: Partial<Record<SaleBannerType, WelcomeGiftStage>> = {
  personal_multi_day: 'age_1',
  personal_one_day: 'age_2',
  personal_last_chance: 'age_3',
};

const getPreTitleKey = (bannerType: SaleBannerType): string | undefined => {
  const keyMap: Partial<Record<SaleBannerType, string>> = {
    personal_multi_day: 'sale.banner.personal_sale_age_1',
    personal_one_day: 'sale.banner.personal_sale_age_2',
    personal_last_chance: 'sale.banner.personal_sale_age_3',
    seasonal_extended: 'sale.banner.extended',
    seasonal_regular: 'sale.banner.limited_time_only',
  };

  return keyMap[bannerType];
};

const getUrgencyKey = (bannerType: SaleBannerType): string | undefined =>
  bannerType === 'last_day'
    ? 'sale.banner.personal_sale_age_3'
    : getPreTitleKey(bannerType);

const getTitleKey = (bannerType: SaleBannerType): string => {
  const keyMap: Record<SaleBannerType, string> = {
    reseller: 'premium-purchase-plan.reseller_sale.header',
    permanent_initial: 'premium-purchase-plan.header.permanent_50.banner',
    permanent_second_upgrade:
      'premium-purchase-plan.header.permanent_50.second_upgrade.banner',
    last_day: 'sale.banner.last_day_countdown',
    personal_multi_day: 'sale.banner.on.select.plans.text',
    personal_one_day: 'sale.banner.on.select.plans.text',
    personal_last_chance: 'sale.banner.on.select.plans.text',
    seasonal_extended: 'sale.banner.on.select.plans.text',
    seasonal_regular: 'sale.banner.on.select.plans.text',
  };

  return keyMap[bannerType];
};

export interface SaleBannerProps {
  /** Overrides for the banner's outer container (e.g. the Quick Pay lightbox's own insets). */
  containerProps?: Partial<EclipseBannerProps>;
  urgencyOnly?: boolean;
}

export const SaleBanner = ({
  containerProps,
  urgencyOnly = false,
}: SaleBannerProps = {}) => {
  const { bannerType, isLoading: isSaleBannerTypeLoading } =
    useSaleBannerType();
  const module = useCurrentModule();
  const { data: discountData } =
    module.SDK.eclipseOfferingApiClient.hooks.useFetchOfferingDiscount({});
  const { resellerBrandingInfo } = useResellerBranding();
  const welcomeGiftAudience = useWelcomeGiftAudience();
  const { t, Trans } = EclipsePackagePickerModule.internals.useTranslations();
  return (
    <PlatformSaleBanner.Root>
      {({ data, isLoading: isSaleBannerLoading }) => {
        const { rate: platformRate, timeLeft } = data ?? {};
        const rate = platformRate ?? discountData?.discount?.rate;

        if (
          isSaleBannerTypeLoading ||
          isSaleBannerLoading ||
          !bannerType ||
          !rate
        ) {
          return null;
        }

        // TODO: Ask from the platform to return the rate without the % symbol
        const numericRate = extractNumericRate(rate);

        const titleKey = getTitleKey(bannerType);
        const preTitleKey = getPreTitleKey(bannerType);
        const hasTimer = Boolean(bannerType === 'last_day' && timeLeft);

        const timer = timeLeft ? (
          <SaleTimer
            hours={timeLeft.hours}
            minutes={timeLeft.minutes}
            seconds={timeLeft.seconds}
          />
        ) : null;

        const welcomeGiftStage = WELCOME_GIFT_STAGES[bannerType];
        const welcomeGiftKey =
          urgencyOnly && welcomeGiftAudience && welcomeGiftStage
            ? WELCOME_GIFT_BANNER_KEYS[welcomeGiftAudience][welcomeGiftStage]
            : undefined;

        if (welcomeGiftKey) {
          return (
            <EclipseBanner {...containerProps}>
              <Box
                align="center"
                verticalAlign="middle"
                display="flex"
                dataHook={DATA_HOOKS.welcomeGiftBanner}
              >
                <Text
                  size="tiny"
                  weight="bold"
                  skin="standard"
                  light={false}
                  className={s.welcomeGiftText}
                >
                  {t(welcomeGiftKey, {
                    defaultValue: miniPpDefault(welcomeGiftKey),
                  })}
                </Text>
              </Box>
            </EclipseBanner>
          );
        }

        const urgencyKey = urgencyOnly ? getUrgencyKey(bannerType) : undefined;

        if (urgencyKey) {
          return (
            <EclipseBanner {...containerProps}>
              <Box
                align="center"
                verticalAlign="middle"
                display="flex"
                gap="10px"
              >
                <span aria-hidden="true" className={s.urgencyDash} />
                <Box display="flex" verticalAlign="middle" gap="4px">
                  <Text
                    weight={hasTimer ? 'normal' : 'bold'}
                    size="medium"
                    skin="standard"
                    light={false}
                    className={s.saleBannerText}
                    dataHook="sale-banner-urgency"
                  >
                    <Trans i18nKey={urgencyKey} />
                  </Text>
                  {hasTimer && <div className={s.urgencyTimer}>{timer}</div>}
                </Box>
                <span aria-hidden="true" className={s.urgencyDash} />
              </Box>
            </EclipseBanner>
          );
        }

        const content = (
          <Text
            weight="normal"
            size="medium"
            skin="standard"
            light={false}
            className={s.saleBannerText}
          >
            {preTitleKey && (
              <>
                <Trans i18nKey={preTitleKey} />
                {' - '}
              </>
            )}
            <Trans
              i18nKey={titleKey}
              values={{
                percent: numericRate,
                days: timeLeft?.days,
                resellerName: resellerBrandingInfo?.name,
              }}
              components={[<Text weight="bold" className={s.saleBannerText} />]}
            />
          </Text>
        );

        if (hasTimer) {
          return (
            <EclipseBanner {...containerProps}>
              <Box
                align="center"
                gap="SP1"
                display="flex"
                textAlign="center"
                verticalAlign="middle"
              >
                {content}
                <Text
                  weight="normal"
                  size="medium"
                  skin="standard"
                  light={false}
                  className={s.saleBannerText}
                >
                  •
                </Text>
                {timer}
              </Box>
            </EclipseBanner>
          );
        }

        return <EclipseBanner {...containerProps}>{content}</EclipseBanner>;
      }}
    </PlatformSaleBanner.Root>
  );
};
